import React from 'react';
import clsx from 'clsx';
import { ClarificationSuggestion } from '@api/assistant/assistantApi';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgRadioChecked from '@icons/radio-checked.svg?react';
import SvgRadioUnchecked from '@icons/radio-unchecked.svg?react';
import SvgArrowRight from '@icons/arrow-right-full.svg?react';
import { AssistantSectionTitle } from './AssistantSection';

interface ComponentProps {
  suggestions: ClarificationSuggestion[];
  /** The suggestion the user went on to ask — shown picked. Matched on text, which is what was sent. */
  chosen?: string;
  /** Only the latest answer's choices can still be picked, and not while a question is in flight. */
  active: boolean;
  onPick: (text: string) => void;
}

/**
 * The choices of a CLARIFICATION answer (Figma 21746:18462): "Co potřebujete?" over one row per
 * suggestion — a radio, the text, and an arrow saying that picking it asks straight away.
 *
 * Picking sends the suggestion's text as the next question. Afterwards the row stays marked, so the
 * conversation still shows which way the user went; earlier answers' choices are inert.
 *
 * Plain buttons rather than RingilButton: the row is a selectable card with content on both sides,
 * which none of RingilButton's modes lay out.
 */
const AssistantSuggestions: React.FC<ComponentProps> = ({ suggestions, chosen, active, onPick }) => {
  const { tr } = useTranslationLgs();
  const sorted = [...suggestions].sort((a, b) => a.rank - b.rank);

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="pt-2">
        <AssistantSectionTitle>{tr('Assistant.clarificationTitle', 'Co potřebujete?')}</AssistantSectionTitle>
      </div>
      {sorted.map(suggestion => {
        const picked = chosen === suggestion.text;
        return (
          <button
            key={`${suggestion.rank}-${suggestion.text}`}
            type="button"
            disabled={!active}
            onClick={() => onPick(suggestion.text)}
            className={clsx(
              'flex w-full items-center gap-2 rounded-lg border border-solid bg-white px-4 py-2 text-left shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)] transition-all duration-200',
              picked ? 'border-content-brand' : 'border-transparent',
              active ? 'cursor-pointer hover:border-content-brand' : 'cursor-default',
            )}
          >
            <div className="flex min-h-10 min-w-0 flex-1 items-center gap-2">
              {picked ? <SvgRadioChecked className="h-4 w-4 shrink-0 text-content-brand" /> : <SvgRadioUnchecked className="h-4 w-4 shrink-0 text-content-tertiary" />}
              <div className="flex min-w-0 flex-col">
                <span className="text-sm text-content-primary">{suggestion.text}</span>
                {suggestion.description && <span className="text-xs text-content-secondary">{suggestion.description}</span>}
              </div>
            </div>
            <SvgArrowRight className="h-[15px] w-[15px] shrink-0 text-content-brand" />
          </button>
        );
      })}
    </div>
  );
};

export default AssistantSuggestions;
